import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const faqs = [
  {
    category: 'Levering & Productie',
    items: [
      {
        q: 'Hoe lang duurt levering uit China?',
        a: 'De productietijd varieert per product, gemiddeld 1–4 weken. Daarna is de verzendtijd via zeecontainer 8–16 weken naar Nederland, of 5–7 werkdagen via lucht. We geven altijd een realistische levertijdschatting in ons voorstel.',
      },
      {
        q: 'Zijn de opgegeven levertijden bindend?',
        a: 'Nee. Alle door DUC gecommuniceerde levertijden zijn indicatief en gebaseerd op schattingen. Levertijden zijn afhankelijk van productietijd, transport, douaneafhandeling en externe factoren. Aan opgegeven levertijden kunnen geen rechten worden ontleend.',
      },
      {
        q: 'Zijn alle kosten inbegrepen bij DDP levering?',
        a: 'Ja. DDP (Delivered Duty Paid) betekent dat transport, invoerrechten en BTW allemaal inbegrepen zijn in de prijs. Je betaalt één all-in bedrag, geen verrassingen achteraf. Indien je kiest voor niet-DDP transport, zijn alle invoerrechten, BTW en bijkomende kosten volledig voor eigen rekening.',
      },
      {
        q: 'Wat gebeurt er als ik niet thuis ben bij de levering?',
        a: 'Indien je niet aanwezig bent bij levering, kan de zending opnieuw worden aangeboden, worden opgeslagen bij het transportbedrijf, of worden doorgestuurd naar een locatie van DUC. Alle bijkomende kosten voor opslag, herlevering en doorzending zijn voor rekening van de klant. Bij herhaaldelijk mislukte levering waarbij de zending retour gaat naar China, dien je opnieuw alle verzendkosten te betalen.',
      },
      {
        q: 'Wat als er vertraging is in de levering?',
        a: 'We houden je voortdurend op de hoogte van de status van jouw order. DUC is echter niet aansprakelijk voor vertragingen veroorzaakt door externe partijen zoals transporteurs, douane of leveranciers. De opgegeven levertijden zijn niet juridisch afdwingbaar.',
      },
    ],
  },
  {
    category: 'Bestellingen, Betaling & Annulering',
    items: [
      {
        q: 'Wat is het minimale aantal (MOQ)?',
        a: 'De MOQ (Minimum Order Quantity) verschilt per product en fabriek. Voor veel producten starten we al vanaf 50 stuks. Er zijn producten met een MOQ van zelfs 1, terwijl andere 10.000 stuks vereisen. We vragen altijd naar jouw behoefte en zoeken fabrieken die passen bij jouw volume.',
      },
      {
        q: 'Kan ik een bestelling annuleren?',
        a: 'Nee. Annulering van bestellingen is niet mogelijk na bevestiging en/of start van productie. Dit geldt in alle gevallen, ongeacht reden of omstandigheden. Zorg er daarom altijd voor dat je zeker bent van je bestelling voordat je akkoord geeft.',
      },
      {
        q: 'Zijn betalingen restitueerbaar?',
        a: 'Nee. Alle betalingen zijn definitief en niet-restitueerbaar. Alle producten worden op maat geproduceerd, waardoor het herroepingsrecht, het recht op retour en het recht op terugbetaling vervallen.',
      },
      {
        q: 'Wanneer wordt mijn bestelling in behandeling genomen?',
        a: 'Bestellingen worden pas in behandeling genomen na (gedeeltelijke of volledige) betaling. Bij niet-tijdige betaling behoudt DUC het recht om leveringen op te schorten en extra kosten in rekening te brengen.',
      },
      {
        q: 'Kan ik meerdere producten combineren in één zending?',
        a: 'Absoluut. We combineren vaak orders van verschillende producten in één container, wat de kosten per product verlaagt. Dit bespreken we tijdens het offerteproces.',
      },
    ],
  },
  {
    category: 'Kwaliteit & Controle',
    items: [
      {
        q: 'Hoe werkt de kwaliteitscontrole?',
        a: 'Kwaliteitscontroles zijn altijd steekproefsgewijs — niet elk individueel product wordt gecontroleerd. Hieraan kunnen geen garanties of rechten worden ontleend. Wij begeleiden de productie en werken alleen met fabrieken die voldoen aan onze kwaliteitsstandaarden.',
      },
      {
        q: 'Kan ik eerst een sample krijgen?',
        a: 'Ja, zeker! Wij raden altijd aan om eerst een sample te bestellen voor je een grote order plaatst. Indien geen sample wordt aangevraagd, ligt het volledige risico bij de klant en worden klachten over kwaliteit of uitvoering na productie niet geaccepteerd.',
      },
      {
        q: 'Wat als de producten niet voldoen aan mijn verwachtingen?',
        a: 'DUC is niet aansprakelijk voor productiefouten, kwaliteitsverschillen of afwijkingen in afwerking. DUC zal waar mogelijk bemiddelen bij problemen, maar geeft geen garanties op oplossingen of compensatie. Indien na levering blijkt dat producten niet voldoen aan verwachtingen, ligt de verantwoordelijkheid bij de klant indien geen sample is afgenomen.',
      },
      {
        q: 'Hebben de producten CE-certificering of andere keurmerken?',
        a: 'Dat hangt af van het product en jouw markt. Voor producten die CE-markering vereisen regelen we de benodigde certificeringen. Geef dit aan bij je aanvraag, dan nemen we dit mee in de selectie van de fabriek.',
      },
    ],
  },
  {
    category: 'Branding & Maatwerk',
    items: [
      {
        q: 'Kan ik mijn eigen logo of branding toevoegen?',
        a: 'Absoluut! Private label en maatwerk is een van onze specialiteiten. We regelen het ontwerp, goedkeuring en productie van producten met jouw branding. Van logo\'s op bekertjes tot volledig custom verpakkingen.',
      },
      {
        q: 'Hoe werkt het ontwerp- en goedkeuringsproces?',
        a: 'Na jouw briefing maken onze partners een first draft. Jij geeft feedback, wij verwerken dit en sturen een nieuw voorstel. Dit gaat door totdat je 100% tevreden bent. Pas dan gaat de productie van start. Let op: na goedkeuring kunnen geen wijzigingen meer worden doorgevoerd.',
      },
      {
        q: 'Mag DUC mijn producten gebruiken voor marketing?',
        a: 'Door een bestelling te plaatsen, geef je toestemming aan DUC om producten, verpakkingen en branding/logo\'s te gebruiken voor marketingdoeleinden, waaronder social media. Indien je dit niet wenst, dien je dit vooraf schriftelijk aan te geven.',
      },
    ],
  },
  {
    category: 'Douane & Import',
    items: [
      {
        q: 'Wie is verantwoordelijk voor invoerrechten en BTW?',
        a: 'Dit hangt af van de gekozen leveringsconditie. Bij DDP-levering zijn invoerrechten, BTW en douanekosten inbegrepen in de prijs. Bij andere leveringscondities zijn alle invoerrechten, BTW en bijkomende kosten volledig voor rekening van de klant. DUC informeert de klant vooraf over eventuele douane- en importverplichtingen.',
      },
      {
        q: 'Is DUC aansprakelijk voor douanevertraging of extra kosten?',
        a: 'Nee. DUC is niet aansprakelijk voor onjuiste inschattingen van douanekosten, vertragingen bij de douane of aanpassingen in wet- en regelgeving. Bij overmacht (zoals politieke of economische omstandigheden) is DUC evenmin aansprakelijk voor vertragingen of tekortkomingen.',
      },
    ],
  },
  {
    category: 'Prijzen & Offerte',
    items: [
      {
        q: 'Hoe snel krijg ik een prijsindicatie?',
        a: 'Binnen 24 uur na jouw aanvraag sturen wij een eerste prijsindicatie. Voor complexere producten of maatwerk kan dit 1–2 werkdagen duren. De prijscheck is volledig gratis en vrijblijvend.',
      },
      {
        q: 'Kunnen prijzen veranderen na een offerte?',
        a: 'Alle offertes zijn vrijblijvend, tenzij schriftelijk anders aangegeven. DUC behoudt het recht om prijzen aan te passen bij valutaschommelingen, veranderingen in transportkosten of aanpassingen vanuit leveranciers. Indien je elders een lagere prijs vindt na bestelling, kan de bestelling niet worden geannuleerd.',
      },
      {
        q: 'Werken jullie ook met particulieren of alleen bedrijven?',
        a: 'Wij werken met zowel bedrijven (B2B) als particulieren. Of je nu een webshop hebt, een horecazaak runt of als particulier iets wilt laten produceren — we helpen iedereen.',
      },
    ],
  },
  {
    category: 'Over DUC',
    items: [
      {
        q: 'Wat is de juridische structuur van DUC?',
        a: 'DUC (Direct uit China) is onderdeel van ROLGO (KVK-nummer: 96233850, BTW-nummer: NL005197054B84). Bestellingen via DUC en ROLGO staan volledig los van elkaar — er bestaat geen aansprakelijkheid of koppeling tussen DUC-orders en ROLGO-orders.',
      },
      {
        q: 'Is DUC een producent of tussenpersoon?',
        a: 'DUC is géén producent van goederen. DUC treedt op als bemiddelaar en sourcingpartner tussen de klant en (voornamelijk Chinese) fabrikanten. DUC regelt sourcing, onderhandelingen, productiebegeleiding, kwaliteitscontrole en logistiek.',
      },
    ],
  },
];

export default function FAQPage() {
  useSEO({
    title: 'Veelgestelde Vragen | Inkopen uit China & Import | DUC - Direct Uit China',
    description: 'Antwoorden op alle vragen over inkopen uit China via DUC. Levertijden, MOQ, kwaliteitscontrole, betaling, annulering en meer. Alles wat u wil weten.',
    keywords: 'FAQ inkopen China, vragen import China, levertijd China, MOQ China, kwaliteitscontrole leverancier, DDP levering vragen',
    canonical: `${SITE_URL}/faq`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${SITE_URL}/faq` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Hoe lang duurt levering uit China?',
            acceptedAnswer: { '@type': 'Answer', text: 'De productietijd varieert per product, gemiddeld 1–4 weken. Daarna is de verzendtijd via zeecontainer 8–16 weken naar Nederland, of 5–7 werkdagen via lucht.' },
          },
          {
            '@type': 'Question',
            name: 'Wat is het minimale aantal (MOQ)?',
            acceptedAnswer: { '@type': 'Answer', text: 'De MOQ verschilt per product en fabriek. Voor veel producten starten we al vanaf 50 stuks. We vragen altijd naar jouw behoefte en zoeken fabrieken die passen bij jouw volume.' },
          },
          {
            '@type': 'Question',
            name: 'Zijn alle kosten inbegrepen bij DDP levering?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja. DDP (Delivered Duty Paid) betekent dat transport, invoerrechten en BTW allemaal inbegrepen zijn in de prijs. Je betaalt één all-in bedrag, geen verrassingen achteraf.' },
          },
          {
            '@type': 'Question',
            name: 'Kan ik een bestelling annuleren?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nee. Annulering van bestellingen is niet mogelijk na bevestiging en/of start van productie. Zorg er altijd voor dat je zeker bent van je bestelling voordat je akkoord geeft.' },
          },
          {
            '@type': 'Question',
            name: 'Hoe werkt de kwaliteitscontrole?',
            acceptedAnswer: { '@type': 'Answer', text: 'Kwaliteitscontroles zijn altijd steekproefsgewijs — niet elk individueel product wordt gecontroleerd. Wij begeleiden de productie en werken alleen met fabrieken die voldoen aan onze kwaliteitsstandaarden.' },
          },
          {
            '@type': 'Question',
            name: 'Hoe snel krijg ik een prijsindicatie?',
            acceptedAnswer: { '@type': 'Answer', text: 'Binnen 24 uur na jouw aanvraag sturen wij een eerste prijsindicatie. De prijscheck is volledig gratis en vrijblijvend.' },
          },
          {
            '@type': 'Question',
            name: 'Wat is de juridische structuur van DUC?',
            acceptedAnswer: { '@type': 'Answer', text: 'DUC (Direct uit China) is onderdeel van ROLGO (KVK-nummer: 96233850, BTW-nummer: NL005197054B84).' },
          },
        ],
      },
    ],
  });

  const [openItem, setOpenItem] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#F7F8FA]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Nav */}
      <header className="bg-[#0A1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png"
              alt="DUC - Direct uit China"
              className="h-[100px] w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-[#3B9EE8] text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Terug naar home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0A1628] pb-20 pt-[100px] sm:pt-[116px] lg:pt-[136px]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-semibold mb-6"
          >
            <i className="ri-question-answer-line" />
            Veelgestelde vragen
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-4xl md:text-5xl font-bold mb-4 leading-tight"
          >
            Alles wat je wil weten
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-white/55 text-lg max-w-xl mx-auto"
          >
            Vind snel antwoord op de meest gestelde vragen over inkopen uit China.
          </motion.p>
        </div>
      </section>

      {/* FAQ content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          {faqs.map((group, gi) => (
            <motion.div
              key={group.category}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: gi * 0.06 }}
              className="mb-12"
            >
              <h2 className="text-[#0A1628] font-bold text-lg mb-5 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3B9EE8]/15 text-[#3B9EE8] text-sm">
                  <i className="ri-price-tag-3-line" />
                </span>
                {group.category}
              </h2>
              <div className="space-y-2">
                {group.items.map((faq, i) => {
                  const key = `${gi}-${i}`;
                  const isOpen = openItem === key;
                  return (
                    <div
                      key={i}
                      className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                        isOpen ? 'border-[#3B9EE8]/40 bg-white' : 'border-[#0A1628]/8 bg-white hover:border-[#3B9EE8]/25'
                      }`}
                    >
                      <button
                        onClick={() => setOpenItem(isOpen ? null : key)}
                        className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer"
                      >
                        <span className={`font-medium text-base pr-8 leading-snug transition-colors ${
                          isOpen ? 'text-[#0A1628] font-semibold' : 'text-[#0A1628]/80'
                        }`}>
                          {faq.q}
                        </span>
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${
                          isOpen ? 'bg-[#3B9EE8] rotate-45' : 'bg-[#0A1628]/6'
                        }`}>
                          <i className={`ri-add-line text-sm transition-colors ${isOpen ? 'text-[#0A1628]' : 'text-[#0A1628]/60'}`} />
                        </div>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.28, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <div className="px-7 pb-6 border-t border-[#3B9EE8]/15">
                              <p className="text-[#0A1628]/65 text-base leading-relaxed pt-4">{faq.a}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}

          {/* AV link */}
          <div className="mb-8 p-5 bg-white rounded-xl border border-[#0A1628]/8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B9EE8]/10 text-[#3B9EE8] flex-shrink-0">
                <i className="ri-file-text-line text-base" />
              </div>
              <div>
                <p className="text-[#0A1628] font-semibold text-sm hover:text-[#3B9EE8] transition-colors cursor-pointer">Meer juridische informatie?</p>
                <p className="text-[#0A1628]/45 text-xs">Lees onze volledige Algemene Voorwaarden</p>
              </div>
            </div>
            <Link
              to="/algemene-voorwaarden"
              className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-semibold"
            >
              Bekijk AV
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-4 p-8 bg-[#0A1628] rounded-2xl text-center">
            <h3 className="text-white font-bold text-xl mb-2">Staat jouw vraag er niet bij?</h3>
            <p className="text-white/50 text-sm mb-6">Neem direct contact op — we reageren binnen 24 uur.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-bold text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Stel je vraag
                <i className="ri-arrow-right-line" />
              </Link>
              <a
                href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China."
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#3B9EE8]/50 text-white/70 hover:text-[#3B9EE8] font-medium text-sm px-6 py-3 rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                Stuur een WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Simple footer */}
      <footer className="bg-[#050E1A] py-8 text-center">
        <p className="text-white/30 text-xs">
          &copy; {new Date().getFullYear()} Direct uit China (DUC). Alle rechten voorbehouden. &nbsp;
          <Link to="/privacybeleid" className="hover:text-white/60 transition-colors">Privacybeleid</Link>
          &nbsp;·&nbsp;
          <Link to="/algemene-voorwaarden" className="hover:text-white/60 transition-colors">Algemene Voorwaarden</Link>
          &nbsp;·&nbsp;
          <Link to="/contact" className="hover:text-white/60 transition-colors">Contact</Link>
        </p>
      </footer>
    </div>
  );
}
